/**
 * PublicProposalPage — tokenized public proposal view (no auth).
 * URL: /proposal/:token
 * The client can view the branded proposal, Accept (with typed e-signature),
 * Decline, or leave a Comment. Every action is logged server-side with IP+UA.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Check, X, MessageSquare, Loader2, AlertCircle, CheckCircle2, FileSignature, FileDown, CreditCard,
} from 'lucide-react';
import { toast } from 'sonner';
import { ProposalDocument } from '../components/ProposalDocument';
import { exportNodeToPdf } from '../lib/proposalPdf';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (cents, currency = 'usd') => {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format((cents || 0) / 100); }
  catch { return `$${((cents || 0) / 100).toFixed(2)}`; }
};

const STATUS_BADGE = {
  accepted: { label: 'Accepted', cls: 'bg-emerald-100 text-emerald-800' },
  declined: { label: 'Declined', cls: 'bg-rose-100 text-rose-800' },
  paid: { label: 'Paid', cls: 'bg-emerald-600 text-white' },
  viewed: { label: 'Viewed', cls: 'bg-indigo-100 text-indigo-800' },
  sent: { label: 'New', cls: 'bg-blue-100 text-blue-800' },
};

export const PublicProposalPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signName, setSignName] = useState('');
  const [signature, setSignature] = useState('');
  const [comment, setComment] = useState('');
  const [commentName, setCommentName] = useState('');
  const [mode, setMode] = useState(null); // 'accept' | 'decline' | 'comment'
  const docRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [verifyingPay, setVerifyingPay] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/public/proposals/${token}`);
      setData(r.data);
    } catch (e) {
      setError(e.response?.status === 404 ? 'This proposal link is invalid or was removed.' : (e.response?.data?.detail || 'Could not load this proposal.'));
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const accept = async () => {
    if (!signName.trim() || !signature.trim()) { toast.error('Enter your name and type your signature'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/proposals/${token}/accept`, { signer_name: signName.trim(), signature: signature.trim() });
      toast.success('Proposal accepted — thank you!');
      setMode(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not accept'); }
    finally { setBusy(false); }
  };

  const decline = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/public/proposals/${token}/decline`, { reason: comment.trim() || null });
      toast.success('Proposal declined');
      setMode(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not decline'); }
    finally { setBusy(false); }
  };

  const sendComment = async () => {
    if (!comment.trim()) { toast.error('Write a comment first'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/public/proposals/${token}/comment`, { name: commentName.trim() || null, body: comment.trim() });
      toast.success('Comment sent');
      setComment(''); setMode(null);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send comment'); }
    finally { setBusy(false); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try { await exportNodeToPdf(docRef.current, `${data.snapshot.number || 'proposal'}.pdf`); }
    catch { toast.error('Could not generate PDF'); }
    finally { setDownloading(false); }
  };

  const payNow = async () => {
    setPaying(true);
    try {
      const r = await axios.post(`${API}/public/proposals/${token}/pay`, { origin: window.location.origin });
      window.location.href = r.data.url; // redirect to Stripe Checkout on the connected account
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not start payment'); setPaying(false); }
  };

  // Handle return from Stripe Checkout (?pay=success&session_id=... or ?pay=cancel)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payResult = params.get('pay');
    const sessionId = params.get('session_id');
    if (!payResult) return;
    const cleanUrl = () => window.history.replaceState({}, '', `/proposal/${token}`);
    if (payResult === 'cancel') { toast('Payment canceled'); cleanUrl(); return; }
    if (payResult === 'success' && sessionId) {
      setVerifyingPay(true);
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const r = await axios.get(`${API}/public/proposals/${token}/pay-status`, { params: { session_id: sessionId } });
          if (r.data.status === 'paid') { toast.success('Payment received — thank you!'); setVerifyingPay(false); cleanUrl(); load(); return; }
        } catch { /* keep polling */ }
        if (tries < 6) setTimeout(poll, 1500);
        else { setVerifyingPay(false); toast('Payment is processing — we\'ll update shortly.'); cleanUrl(); }
      };
      poll();
    }
  }, [token, load]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-indigo-600" /></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="text-center" data-testid="public-proposal-error">
        <AlertCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
        <p className="text-zinc-700">{error}</p>
      </div>
    </div>
  );

  const s = data.snapshot;
  const brandColor = s.branding?.color || '#4f46e5';
  const responded = data.responded;
  const badge = STATUS_BADGE[data.status];

  const oneTime = s.totals?.one_time_total_cents || 0;
  const isDeposit = s.payment_option === 'deposit' && s.deposit_pct;
  const payAmount = isDeposit ? Math.round(oneTime * s.deposit_pct / 100) : oneTime;
  const canPay = data.status === 'accepted' && payAmount >= 50;

  return (
    <div className="min-h-screen bg-zinc-50 py-10 px-4" data-testid="public-proposal-page">
      <div className="max-w-2xl mx-auto space-y-5">
        {/* Header / branding */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {s.branding?.logo_url
              ? <img src={s.branding.logo_url} alt="logo" className="h-11 w-11 rounded object-contain bg-white border border-zinc-200" />
              : <div className="h-11 w-11 rounded flex items-center justify-center text-white" style={{ background: brandColor }}><FileSignature className="w-5 h-5" /></div>}
            <div>
              <p className="font-semibold text-zinc-900">{s.branding?.company_name || 'Proposal'}</p>
              <p className="text-xs text-zinc-500">{s.number}</p>
            </div>
          </div>
          {badge && <Badge className={badge.cls} data-testid="public-status-badge">{badge.label}</Badge>}
        </div>

        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={downloadPdf} disabled={downloading} data-testid="download-pdf-btn">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />}Download PDF
          </Button>
        </div>

        {canPay && (
          <Card className="border-2" style={{ borderColor: brandColor }} data-testid="pay-card">
            <CardContent className="p-5 flex items-center justify-between gap-4 flex-wrap">
              <div>
                <p className="font-semibold text-zinc-900">{isDeposit ? `Pay ${s.deposit_pct}% deposit` : 'Complete your payment'}</p>
                <p className="text-sm text-zinc-500">Amount due now: <strong>{money(payAmount, s.currency)}</strong></p>
              </div>
              <Button onClick={payNow} disabled={paying || verifyingPay} style={{ background: brandColor }} className="text-white" data-testid="pay-now-btn">
                {(paying || verifyingPay) ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                {verifyingPay ? 'Verifying…' : `Pay ${money(payAmount, s.currency)}`}
              </Button>
            </CardContent>
          </Card>
        )}

        {data.status === 'paid' && (
          <Card className="border-emerald-300 bg-emerald-50" data-testid="paid-banner">
            <CardContent className="p-4 flex items-center gap-2 text-emerald-800">
              <CheckCircle2 className="w-5 h-5" />
              <span className="text-sm font-medium">Payment received — thank you! This proposal is fully paid.</span>
            </CardContent>
          </Card>
        )}

        {responded && data.status !== 'paid' && (
          <Card className="border-emerald-200 bg-emerald-50" data-testid="responded-banner">
            <CardContent className="p-4 flex items-center gap-2 text-emerald-800">
              <CheckCircle2 className="w-5 h-5" />
              <span className="text-sm">You have already {data.status} this proposal.</span>
            </CardContent>
          </Card>
        )}

        <Card className="overflow-hidden">
          <ProposalDocument ref={docRef} snap={s} />
        </Card>

        {/* Actions */}
        {!responded && (
          <Card data-testid="public-actions">
            <CardContent className="p-4 space-y-4">
              {mode === 'accept' ? (
                <div className="space-y-3" data-testid="accept-form">
                  <p className="text-sm text-zinc-600">Type your name to sign and accept this proposal.</p>
                  <div><Label>Full name</Label><Input value={signName} onChange={(e) => setSignName(e.target.value)} data-testid="signer-name-input" /></div>
                  <div><Label>Signature (type your name)</Label><Input value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="e.g. Jane Doe" className="font-[cursive]" data-testid="signature-input" /></div>
                  <div className="flex gap-2">
                    <Button onClick={accept} disabled={busy} style={{ background: brandColor }} className="text-white" data-testid="confirm-accept-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}Sign &amp; Accept</Button>
                    <Button variant="ghost" onClick={() => setMode(null)}>Cancel</Button>
                  </div>
                </div>
              ) : mode === 'decline' ? (
                <div className="space-y-3" data-testid="decline-form">
                  <div><Label>Reason (optional)</Label><Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="decline-reason-input" /></div>
                  <div className="flex gap-2">
                    <Button variant="destructive" onClick={decline} disabled={busy} data-testid="confirm-decline-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}Decline</Button>
                    <Button variant="ghost" onClick={() => setMode(null)}>Cancel</Button>
                  </div>
                </div>
              ) : mode === 'comment' ? (
                <div className="space-y-3" data-testid="comment-form">
                  <div><Label>Your name (optional)</Label><Input value={commentName} onChange={(e) => setCommentName(e.target.value)} data-testid="comment-name-input" /></div>
                  <div><Label>Comment</Label><Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="comment-body-input" /></div>
                  <div className="flex gap-2">
                    <Button onClick={sendComment} disabled={busy} data-testid="confirm-comment-btn">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageSquare className="w-4 h-4 mr-2" />}Send comment</Button>
                    <Button variant="ghost" onClick={() => setMode(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => setMode('accept')} style={{ background: brandColor }} className="text-white" data-testid="accept-btn"><Check className="w-4 h-4 mr-2" />Accept</Button>
                  <Button variant="outline" onClick={() => setMode('decline')} data-testid="decline-btn"><X className="w-4 h-4 mr-2" />Decline</Button>
                  <Button variant="ghost" onClick={() => setMode('comment')} data-testid="comment-btn"><MessageSquare className="w-4 h-4 mr-2" />Comment</Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}
        <p className="text-center text-xs text-zinc-400">Powered by PostToSocial</p>
      </div>
    </div>
  );
};

export default PublicProposalPage;
